"use client";

import {
  createContext,
  useContext,
  useId,
  type ReactNode,
} from "react";
import styles from "./Form.module.css";

/**
 * Field — wrapper dla pojedynczego pola formularza z FLOATING LABEL.
 *
 * **Layout (krytyczne dla floating):**
 *   - `<div.field>` ma `position: relative` (parent dla absolute label)
 *   - `<input>` / `<textarea>` / `<select>` jest PIERWSZY w DOM
 *   - `<label>` jest PO control (CSS sibling selector `~` wymaga)
 *   - `<span.errorText>` / `<span.hint>` — pod control
 *
 * **A11y wiring (przez `FieldContext`):**
 *   - `id` generowany przez `useId()` propaguje do control jako id + htmlFor
 *   - `aria-describedby` wiąże control z error/hint span
 *   - `aria-invalid` na control gdy error
 *   - `aria-required` na control gdy required
 *
 * **Auto placeholder=" ":** primitywy Input/Textarea wewnątrz Field czytają
 * Context i wstrzykują `placeholder=" "` (pusty space) — to **bezwzględnie
 * wymagane** żeby selektor `:placeholder-shown` działał. Konsument może
 * nadpisać przez własny `placeholder="..."` prop.
 *
 * **Prefix/suffix + PhoneInput:** floating labels działają również gdy
 * Input ma `prefix`/`suffix` lub gdy children to `PhoneInput`. Form.module.css
 * używa `:has()` żeby (a) animować label gdy control wewnątrz wrappera jest
 * focused/wypełniony, (b) offsetować label w prawo przez `--label-offset`
 * żeby nie nakładał się na ikonę prefix-u albo selektor kraju.
 */
export function Field({
  label,
  required,
  optional,
  error,
  hint,
  successMessage,
  children,
  className,
}: FieldProps) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const successId = successMessage ? `${id}-success` : undefined;

  const describedBy =
    [errorId, hintId, successId].filter(Boolean).join(" ") || undefined;

  const ctxValue: FieldContextValue = {
    id,
    describedBy,
    invalid: !!error,
    required: !!required,
    /* Flag dla Input/Textarea — wewnątrz Field użyj placeholder=" "
       gdy konsument nie podał własnego. */
    inField: true,
  };

  return (
    <FieldContext.Provider value={ctxValue}>
      <div className={`${styles.field}${className ? ` ${className}` : ""}`}>
        {children}

        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            )}
            {optional && !required && (
              <span className={styles.optional}>(opcjonalne)</span>
            )}
          </label>
        )}

        {error && (
          <span id={errorId} className={styles.errorText} role="alert">
            {error}
          </span>
        )}
        {!error && successMessage && (
          <span id={successId} className={styles.successText}>
            {successMessage}
          </span>
        )}
        {hint && !error && (
          <span id={hintId} className={styles.hint}>
            {hint}
          </span>
        )}
      </div>
    </FieldContext.Provider>
  );
}

export type FieldProps = {
  /** Tekst floating label. Bind do kontrolki przez `htmlFor`. */
  label?: ReactNode;
  /** Pokaż czerwoną gwiazdkę przy label-u + ustaw aria-required. */
  required?: boolean;
  /** Pokaż "(opcjonalne)" przy label-u — ignored gdy `required`. */
  optional?: boolean;
  /** Komunikat błędu pod kontrolką. Aktywuje stan `invalid` i aria-invalid. */
  error?: string | null;
  /** Komunikat sukcesu pod kontrolką (np. po async walidacji). */
  successMessage?: string | null;
  /** Tekst pomocniczy pod kontrolką (ignorowany gdy error obecny). */
  hint?: ReactNode;
  /** Pojedyncza kontrolka — Input / Textarea / Select / custom JSX. */
  children: ReactNode;
  /** Dodatkowa klasa (np. dla grid-cell layout). */
  className?: string;
};

/* ─── Context ─────────────────────────────────────────────────────────── */

type FieldContextValue = {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  /** True gdy primitive jest wewnątrz Field — aktywuje floating placeholder. */
  inField: boolean;
};

const FieldContext = createContext<FieldContextValue | null>(null);

/** Hook dla primitywów Input/Textarea/Select — czytają auto-id z Fielda. */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}
